/**
 * Responsibility: the part of a holding only the operator can know.
 *
 * Split out of `HoldingDialog` to keep that file to the job of being a
 * dialog. Nothing on this form comes from a catalogue: a certificate
 * number names this exact copy rather than the card, a purchase date is
 * not the date the row was created, and a cost basis is the only thing
 * that turns a value into a profit.
 *
 * The slab barcode can be scanned instead of typed, because a ten-digit
 * number copied by eye off a label is the single most error-prone thing
 * this form asks for. Only a plausible certificate is accepted from the
 * scanner - see `certNumberOf` - so a UPC read off the wrong thing cannot
 * end up sitting in this field looking like a real cert.
 */
import { useState } from 'react';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import ScanIcon from '@mui/icons-material/QrCodeScannerOutlined';
import { ScrollRegion } from '../cards/CardDialogShell';
import { certNumberOf } from '../scan/barcodes';
import { ScanDialog } from '../scan/ScanDialog';
import { GRADING_COMPANIES } from './gradingColours';

/** Every field as text, because that is what the inputs hold. */
export type HoldingForm = {
  company: string;
  grade: string;
  cert: string;
  acquired: string;
  cost: string;
  quantity: string;
  notes: string;
};

type HoldingDetailsFormProps = {
  form: HoldingForm;
  onChange: <K extends keyof HoldingForm>(key: K, value: HoldingForm[K]) => void;
};

export function HoldingDetailsForm({ form, onChange }: HoldingDetailsFormProps) {
  const [scanning, setScanning] = useState(false);
  // An empty grader means a raw copy, which is the one state where a
  // grade and a certificate mean nothing.
  const raw = form.company === '';

  return (
    <>
      <ScrollRegion>
        <Stack spacing={2} sx={{ pt: 0.5 }}>
          <Typography variant="subtitle2">What you own</Typography>

          <Box
            sx={{
              display: 'grid',
              gap: 1.5,
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
            }}
          >
            <TextField
              select
              size="small"
              label="Graded by"
              value={form.company}
              onChange={(event) => onChange('company', event.target.value)}
              helperText="Blank for a raw copy."
            >
              <MenuItem value="">Raw (ungraded)</MenuItem>
              {GRADING_COMPANIES.map((name) => (
                <MenuItem key={name} value={name}>
                  {name}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              size="small"
              label="Grade"
              value={form.grade}
              onChange={(event) => onChange('grade', event.target.value)}
              placeholder={raw ? 'Ungraded' : '10'}
              helperText={raw ? 'Left as ungraded.' : 'Half grades welcome: 9.5.'}
            />

            <TextField
              size="small"
              label="Certificate number"
              value={form.cert}
              onChange={(event) =>
                onChange('cert', event.target.value.replace(/[^0-9A-Za-z-]/g, ''))
              }
              disabled={raw}
              helperText={raw ? 'Slabs only.' : 'The number on the slab, or scan its barcode.'}
              slotProps={{
                input: {
                  endAdornment: raw ? null : (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        edge="end"
                        aria-label="Scan the slab barcode"
                        onClick={() => setScanning(true)}
                      >
                        <ScanIcon fontSize="small" />
                      </IconButton>

                    </InputAdornment>
                  ),
                },
              }}
            />

            <TextField
              size="small"
              type="date"
              label="Bought on"
              value={form.acquired}
              onChange={(event) => onChange('acquired', event.target.value)}
              slotProps={{ inputLabel: { shrink: true } }}
              helperText="Not the date you catalogued it."
            />

            <TextField
              size="small"
              label="Paid each"
              value={form.cost}
              onChange={(event) =>
                onChange('cost', event.target.value.replace(/[^0-9.]/g, ''))
              }
              slotProps={{
                input: { startAdornment: <InputAdornment position="start">$</InputAdornment> },
              }}
              helperText="Blank means unknown, not zero."
            />

            <TextField
              size="small"
              label="How many"
              value={form.quantity}
              onChange={(event) =>
                onChange('quantity', event.target.value.replace(/[^0-9]/g, ''))
              }
            />
          </Box>

          <TextField
            size="small"
            label="Notes"
            value={form.notes}
            onChange={(event) => onChange('notes', event.target.value)}
            multiline
            minRows={3}
          />
        </Stack>
      </ScrollRegion>

      <ScanDialog
        open={scanning}
        title="Scan the slab"
        hint="Point the camera at the barcode on the PSA label. It encodes the certificate number."
        onClose={() => setScanning(false)}
        onScanned={(code) => {
          const found = certNumberOf(code);
          if (found !== null) onChange('cert', found);
          setScanning(false);
        }}
      />
    </>
  );
}
